import { useCallback, useContext, useEffect, useState } from 'react';

// Components
import { PaymentTypes } from '@/components/PaymentTypes/PaymentTypes';
import { ProductDescription } from '@/components/ProductDescription/ProductDescription';
import { ProductHeading } from '@/components/ProductHeading/ProductHeading';
import { ProductImage } from '@/components/ProductImage/ProductImage';
import { SmartDescription } from '@/components/SmartDescription/SmartDescription';
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
import Auth from '@/lib/authentication';
// Modules
import { getProductImage, getProductName, isSmart } from '@/lib/util';
import { checkAlreadyAcquiredEntitlements } from '@/lib/http';
import { error as errorLog } from '@/lib/log';
import { decideBanner, type BannerActionType } from '@/lib/banner';

import { ProductAttributes } from '../ProductAttributes/ProductAttributes';
import { ProductActionSection } from '../ProductActionSection/ProductActionSection';

type Props = {
  isEligibilityChecked: boolean;
  setDidClickNext: React.Dispatch<React.SetStateAction<boolean>>;
  showActiveProductWarning: boolean;
};

const ProductDetails = ({ isEligibilityChecked, setDidClickNext, showActiveProductWarning }: Props) => {
  const [state, dispatch] = useContext(Context);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [entitlementsAlreadyAcquired, setEntitlementsAlreadyAcquired] = useState(false);
  const [isEntitlementCheckDone, setIsEntitlementCheckDone] = useState(false);
  const [bannerAction, setBannerAction] = useState<BannerActionType>('none');
  const [showSmartPlusPurHint, setShowSmartPlusPurHint] = useState(false);

  // Checks if entitlements already exist from a print subscription (P7)
  useEffect(() => {
    void (async () => {
      if (!(await Auth.userIsLoggedIn())) {
        return;
      }

      try {
        const response = await checkAlreadyAcquiredEntitlements('p7');

        const hasP7Entitlements = JSON.parse(response.subscription);
        setEntitlementsAlreadyAcquired(hasP7Entitlements);
        setIsEntitlementCheckDone(true);

        if (response.clientNo) {
          dispatch({
            type: ActionTypes.printAccountNumberChange,
            payload: {
              key: 'printAccountNumber',
              value: response.clientNo
            }
          });
        }
      } catch (e) {
        errorLog({ e });
        setEntitlementsAlreadyAcquired(false);
        setIsEntitlementCheckDone(true);
      }
    })().catch((error) => {
      errorLog('Unexpected error checking entitlements:', error);
      setEntitlementsAlreadyAcquired(false);
      setIsEntitlementCheckDone(true);
    });
  }, [state.bundle, dispatch]);

  // Determine which banner(s) to show once entitlement check and upgrade data are available
  useEffect(() => {
    if (!isEntitlementCheckDone) return;

    const banner = decideBanner({
      bundle: state.bundle,
      hasP7Entitlements: entitlementsAlreadyAcquired,
      isLoggedIn: true,
      moveFromBundleRef: state.upgrade?.moveFromBundleRef
    });

    setBannerAction(banner.action);
    setShowSmartPlusPurHint(banner.showSmartPlusPurHint ?? false);
  }, [state.bundle, entitlementsAlreadyAcquired, isEntitlementCheckDone, state.upgrade?.moveFromBundleRef]);

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();

      setIsSubmitted(true);
      setDidClickNext(true);

      dispatch({
        type: ActionTypes.disableForm,
        payload: {
          key: 'disableOnSubmit',
          value: true
        }
      });

      // See Product-Komponent for Event Handler
      await Auth.signIn();
    },
    [dispatch, setDidClickNext]
  );

  const productName = state.bundle ? getProductName(state.bundle) : '';

  const productImage = state.bundle ? getProductImage(state.bundle) : '';

  return (
    <>
      {productImage ? (
        <ProductImage
          productImage={productImage}
          productName={productName}
        />
      ) : null}
      {productName ? <ProductHeading productName={productName} /> : null}

      <div className="product-content">
        {state.bundle && isSmart(state.bundle) ? <SmartDescription /> : <ProductDescription />}

        <ProductAttributes
          bannerAction={bannerAction}
          showActiveProductWarning={showActiveProductWarning}
        />

        <ProductActionSection
          bannerAction={bannerAction}
          bundle={state.bundle}
          entitlementsAlreadyAcquired={entitlementsAlreadyAcquired}
          handleClick={(e) => void handleClick(e)}
          isEligibilityChecked={isEligibilityChecked}
          isSubmitted={isSubmitted}
          pricingTextLength={state.pricingText.length}
          printAccountNumber={state.printAccountNumber}
          showActiveProductWarning={showActiveProductWarning}
          showSmartPlusPurHint={showSmartPlusPurHint}
        />

        {state.bundle?.contractAllowedPaymentMethods && state.currentPage === 'productPage' ? (
          <PaymentTypes paymentMethods={state.bundle.contractAllowedPaymentMethods} />
        ) : (
          ''
        )}
      </div>
    </>
  );
};

export { ProductDetails };
